import { ensureItemsModalState } from './state.js';

function getItemContentHTML(context, item) {
    if (item && item.rendered_html) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = item.rendered_html;
        const fdiItem = tempDiv.querySelector('.fdi-item');
        const container = fdiItem || tempDiv;

        container.querySelectorAll('a[href]').forEach((link) => {
            link.setAttribute('target', '_blank');
            link.setAttribute('rel', 'noopener noreferrer');
        });

        container.querySelectorAll('.save-btn').forEach((button) => button.remove());

        return container.innerHTML;
    }

    return context.renderItemManually(item);
}

export function renderCollectionItems(items, collection) {
    const listContainer = document.getElementById('items-list');
    const countInfo = document.getElementById('items-showing');

    if (!listContainer) {
        return;
    }

    if (!items || items.length === 0) {
        document.getElementById('items-empty')?.classList.remove('hidden');
        document.getElementById('items-list')?.classList.add('hidden');
        this.toggleBulkActionsHeader();
        return;
    }

    if (countInfo) {
        countInfo.textContent = items.length;
    }

    const modal = document.getElementById('collection-items-modal');
    modal?.setAttribute('data-collection-id', collection.id);

    const state = ensureItemsModalState(this);
    state.filteredItems = Array.isArray(items) ? [...items] : [];
    state.cachedElements = [];

    const fragment = document.createDocumentFragment();
    items.forEach((item) => {
        const element = this.composeModalItemElement(item, collection);
        if (!element) {
            return;
        }

        const titleElement = element.querySelector('h3');
        const titleText = titleElement?.textContent || '';
        if (titleElement) {
            titleElement.dataset.originalTitle = titleText;
        }

        state.cachedElements.push({
            element,
            fdiId: String(item.fdi_id || item.id),
            title: titleText.toLowerCase(),
        });

        fragment.appendChild(element);
    });

    listContainer.innerHTML = '';
    listContainer.appendChild(fragment);

    document.getElementById('items-empty')?.classList.add('hidden');
    listContainer.classList.remove('hidden');
    this.attachToggleListeners();
}

export function updateItemsCount() {
    const listContainer = document.getElementById('items-list');
    const countElement = document.getElementById('items-showing');
    if (!listContainer || !countElement) {
        return;
    }

    const visibleItems = listContainer.querySelectorAll('.fdi-item:not(.hidden)');
    countElement.textContent = visibleItems.length;
}

export function toggleBulkActionsHeader() {
    const state = ensureItemsModalState(this);
    const header = document.querySelector('.bulk-actions-header');
    const deleteButton = document.getElementById('bulk-delete-btn');
    const selectedCount = document.getElementById('selected-count');
    const selectAllCheckbox = document.getElementById('select-all-items');

    const visibleIds = state.cachedElements
        .filter(({ element }) => !element.classList.contains('hidden'))
        .map(({ fdiId }) => fdiId);

    if (selectAllCheckbox) {
        if (visibleIds.length === 0) {
            selectAllCheckbox.checked = false;
            selectAllCheckbox.indeterminate = false;
        } else {
            const selectedVisible = visibleIds.filter((id) => state.selectedIds.has(id));
            const allSelected = selectedVisible.length === visibleIds.length;
            selectAllCheckbox.checked = allSelected;
            selectAllCheckbox.indeterminate = selectedVisible.length > 0 && !allSelected;
        }
    }

    if (selectedCount) {
        selectedCount.textContent = state.selectedIds.size;
    }

    if (state.selectedIds.size > 0) {
        header?.classList.remove('hidden');
        deleteButton?.removeAttribute('disabled');
    } else {
        header?.classList.add('hidden');
        deleteButton?.setAttribute('disabled', 'disabled');
    }
}

export function composeModalItemElement(item, collection) {
    const state = ensureItemsModalState(this);
    const fdiId = String(item.fdi_id || item.id || '');
    if (!fdiId) {
        return null;
    }

    const wrapper = document.createElement('div');
    wrapper.className = 'fdi-item relative pl-12 pr-4 py-4 transition-colors duration-150 hover:bg-gray-50';
    wrapper.dataset.fdiId = fdiId;
    wrapper.setAttribute('tabindex', '0');

    const checkboxContainer = document.createElement('div');
    checkboxContainer.className = 'absolute left-4 top-4';

    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.className = 'item-checkbox h-4 w-4 text-mps-blue-600 focus:ring-mps-blue-500 border-gray-300 rounded';
    checkbox.dataset.fdiId = fdiId;
    checkbox.checked = state.selectedIds.has(fdiId);
    checkbox.setAttribute('aria-label', 'Chọn dự án');
    checkboxContainer.appendChild(checkbox);

    const removeBtn = document.createElement('button');
    removeBtn.className = 'remove-item-btn absolute right-4 top-4 p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors';
    removeBtn.dataset.fdiId = fdiId;
    removeBtn.setAttribute('type', 'button');
    removeBtn.setAttribute('aria-label', 'Xóa khỏi collection');
    removeBtn.innerHTML = `
        <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M18 6L6 18" />
            <path d="M6 6l12 12" />
        </svg>
    `;

    const contentContainer = document.createElement('div');
    contentContainer.className = 'item-content';
    contentContainer.innerHTML = getItemContentHTML(this, item, collection);

    wrapper.appendChild(checkboxContainer);
    wrapper.appendChild(removeBtn);
    wrapper.appendChild(contentContainer);

    return wrapper;
}

/* eslint-disable no-param-reassign */
export function renderItemManually(item) {
    const formatCapital = (amount) => {
        if (!amount || amount <= 0) return null;

        /* Detect if this is a DDI item based on collection type or item properties */
        const isDDI = item.collection_type === 'ddi' || item.post_type === 'ddi' || 
                     (item.registered_capital && item.registered_capital > 1000000000); // DDI typically has larger amounts

        if (isDDI) {
              /* DDI uses VND formatting with Vietnamese abbreviations - no decimal places */
              if (amount >= 1000000000) {
                  const formatted = Math.floor(amount / 1000000000);
                  return formatted.toLocaleString('vi-VN') + ' tỷ VND';
              } else if (amount >= 1000000) {
                  const formatted = Math.floor(amount / 1000000);
                  return formatted.toLocaleString('vi-VN') + ' triệu VND';
              } else if (amount >= 1000) {
                  const formatted = Math.floor(amount / 1000);
                  return formatted.toLocaleString('vi-VN') + ' nghìn VND';
              }
              return Math.floor(amount).toLocaleString('vi-VN') + ' VND';
        } else {
            /* FDI uses USD formatting */
            if (amount >= 1000000000) {
                return '$' + (amount / 1000000000).toFixed(2).replace(/\.?0+$/, '') + 'B';
            } if (amount >= 1000000) {
                return '$' + (amount / 1000000).toFixed(2).replace(/\.?0+$/, '') + 'M';
            } if (amount >= 1000) {
                return '$' + (amount / 1000).toFixed(2).replace(/\.?0+$/, '') + 'K';
            }
            return '$' + new Intl.NumberFormat('en-US', { minimumFractionDigits: 0 }).format(amount);
        }
    };

    let capitalHTML = '';
    if (item.registered_capital && item.registered_capital > 0) {
        const formattedRegistered = formatCapital(item.registered_capital);
        const breakdownParts = [];

        if (item.contributed_capital && item.contributed_capital > 0) {
            breakdownParts.push(`Góp: ${formatCapital(item.contributed_capital)}`);
        }
        if (item.loan_capital && item.loan_capital > 0) {
            breakdownParts.push(`Vay: ${formatCapital(item.loan_capital)}`);
        }

        capitalHTML = `
            <div class="flex gap-2">
                <span class="whitespace-nowrap font-normal">Vốn đăng ký:</span>
                <span class="capital_value" data-value="${item.registered_capital}">
                    ${formattedRegistered}${breakdownParts.length > 0 ? ` <span class="text-gray-500">(${breakdownParts.join(' + ')})</span>` : ''}
                </span>
            </div>
        `;
    }

    const issueYear = item.issue_date ? new Date(item.issue_date).getFullYear() : null;
    const location = item.location || item.industry_location || '';

    let locationYearHTML = '<div class="flex gap-2">';
    if (location) {
        locationYearHTML += `<p class="line-clamp-1">${this.escapeHtml(location)}</p>`;
    }
    if (issueYear) {
        locationYearHTML += `<span class="text-gray-400"> / </span><span>Năm cấp:</span><span>${issueYear}</span>`;
    }
    locationYearHTML += '</div>';

    let objectiveHTML = '';
    const projectObjective = item.project_objective || '';
    if (projectObjective) {
        const safeText = this.escapeHtml(projectObjective);
        const needsToggle = safeText.length > 50;

        if (!needsToggle) {
            objectiveHTML = `
                <div class="project-objective">
                    <div class="flex gap-2">
                        <span class="whitespace-nowrap font-normal">Mục tiêu:</span>
                        <div class="break-words line-clamp-1">${safeText}</div>
                    </div>
                </div>
            `;
        } else {
            objectiveHTML = `
                <div class="project-objective">
                    <div class="flex gap-2">
                        <span class="whitespace-nowrap font-normal">Mục tiêu:</span>

                        <div class="collapsed-layout flex items-center gap-1" data-layout="collapsed">
                            <div class="break-words line-clamp-1 flex-1" data-fdi-collapsible>${safeText}</div>
                            <button type="button" class="text-xs text-blue-600 hover:underline whitespace-nowrap" data-fdi-toggle>Xem thêm</button>
                        </div>

                        <div class="expanded-layout hidden" data-layout="expanded">
                            <div class="break-words" data-fdi-collapsible>${safeText}</div>
                        </div>
                    </div>
                </div>
            `;
        }
    }

    let countryFlagHTML = '';
    if (item.country && item.country.flag_url) {
        countryFlagHTML = `
            <div class="country flag block absolute top-4 right-4 w-[30px] aspect-[3/2] overflow-hidden border border-gray-200 rounded-sm"
                 data-name="${this.escapeHtml(item.country.slug || '')}"
                 title="${this.escapeHtml(item.country.name || '')}"
                 aria-label="${this.escapeHtml(item.country.name || '')}">
                <img src="${this.escapeHtml(item.country.flag_url)}"
                     class="w-full h-full object-cover"
                     loading="lazy"
                     alt="${this.escapeHtml(item.country.name || '')}" />
            </div>
        `;
    }

    const projectName = item.project_name || item.post_title || item.title || 'Chưa có tên dự án';
    return `
        ${countryFlagHTML}

        <h3 class="text-sm font-semibold !capitalize line-clamp-2">${this.escapeHtml(projectName)}</h3>

        <div class="fdi-details text-sm text-gray-600">
            ${locationYearHTML}
            <div class="capital">
                ${capitalHTML}
            </div>
            <div class="meta">
                ${objectiveHTML}
            </div>
        </div>
    `;
}
/* eslint-enable no-param-reassign */

export function attachItemsListeners() {
    return;
}

export function attachToggleListeners() {
    const modal = document.getElementById('collection-items-modal');
    if (!modal) return;

    modal.removeEventListener('click', handleToggleClick);
    modal.addEventListener('click', handleToggleClick);
}

function handleToggleClick(event) {
    const btn = event.target.closest('[data-fdi-toggle]');
    if (!btn) return;

    event.preventDefault();
    event.stopPropagation();

    const fdiItem = btn.closest('.fdi-item');
    if (!fdiItem) return;

    const collapsedLayout = fdiItem.querySelector('[data-layout="collapsed"]');
    const expandedLayout = fdiItem.querySelector('[data-layout="expanded"]');

    if (!collapsedLayout || !expandedLayout) return;

    const isExpanded = expandedLayout.classList.contains('hidden');

    if (isExpanded) {
        collapsedLayout.classList.add('hidden');
        expandedLayout.classList.remove('hidden');

        const expandedContent = expandedLayout.querySelector('[data-fdi-collapsible]');
        if (expandedContent) {
            const inlineBtn = document.createElement('button');
            inlineBtn.type = 'button';
            inlineBtn.className = 'text-xs text-blue-600 hover:underline whitespace-nowrap ml-1';
            inlineBtn.setAttribute('data-fdi-toggle', '');
            inlineBtn.textContent = 'Thu gọn';
            expandedContent.appendChild(inlineBtn);
        }
    } else {
        expandedLayout.classList.add('hidden');
        collapsedLayout.classList.remove('hidden');

        const inlineBtns = expandedLayout.querySelectorAll('[data-fdi-toggle]');
        inlineBtns.forEach((button) => button.remove());
    }
}

export function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

